import {evaluationIsVariant} from '@homeaway/react-experiment';
import {createSelector} from 'reselect';
import {getIsMobile} from './system';
import {getAbacusTests} from './store';
import {getTravelerSegments} from './travelerSegmentFeatures';
import {getMultiUnitsLayoutValues} from './multiUnits';
import {VRBO_WEB_PDP_POLICIES_SECTION} from '../abacus/experiments';

const MAIN_CONTENT_SECTIONS = {
    amenities: 'amenities',
    map: 'map',
    overview: 'overview',
    rates_availability: 'rates-availability',
    reviews: 'reviews',
    other_units: 'other-units',
    policies: 'policies-nav'
};

const getOtherUnitsSection = ({showMultiUnits, isHotelType}, isMidPage) => {
    return showMultiUnits && isHotelType === isMidPage
        ? // !XOR, return section at midpage for hotels case AND return section at bottom of the page for other listing types
          [
              {
                  name: MAIN_CONTENT_SECTIONS.other_units,
                  elements: ['other-units']
              }
          ]
        : [];
};

const getPoliciesTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_POLICIES_SECTION], 1);
    }
);

const getDesktopLayout = createSelector(
    [getTravelerSegments, getMultiUnitsLayoutValues, getPoliciesTestOn],
    ({coldStartUser}, multiUnitsLayoutValues, policiesTestOn) => {
        return {
            spacingBelow: {
                default: '32px',
                'listing-summary-desktop': '16px',
                'trip-details': '16px',
                'about-the-property': '88px',
                'inline-virtual-tour': '88px',
                'unit-spaces': '88px',
                'midpage-reco-carousel': '88px',
                'chat-ui': '88px',
                'owner-details': '88px',
                amenities: '88px',
                'house-rules': '56px',
                'cancellation-policy': '16px',
                'cancellation-policy-with-trips-link': '40px',
                'damage-policy-policies-section': '40px',
                'policies-header': '24px',
                'damage-policy': '16px'
            },
            order: [
                {
                    name: MAIN_CONTENT_SECTIONS.overview,
                    elements: [
                        'inline-urgency',
                        'listing-summary-desktop',
                        'trip-details',
                        'about-the-property',
                        'inline-virtual-tour',
                        'unit-spaces',
                        coldStartUser && !multiUnitsLayoutValues.showMultiUnits
                            ? 'midpage-reco-carousel'
                            : null,
                        'chat-ui',
                        'owner-details',
                        'auth-card'
                    ]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.amenities,
                    elements: ['amenities'].concat(
                        policiesTestOn
                            ? []
                            : [
                                  'house-rules',
                                  'cancellation-policy',
                                  'damage-policy',
                                  'cleaning-practices'
                              ]
                    )
                },
                ...(policiesTestOn
                    ? [
                          {
                              name: MAIN_CONTENT_SECTIONS.policies,
                              elements: [
                                  'policies-header',
                                  'cancellation-policy-with-trips-link',
                                  'damage-policy-policies-section',
                                  'house-rules-wide',
                                  'cleaning-practices'
                              ]
                          }
                      ]
                    : []),
                ...getOtherUnitsSection(multiUnitsLayoutValues, true),
                {
                    name: MAIN_CONTENT_SECTIONS.reviews,
                    elements: ['traveler-sentiment-highlights', 'reviews']
                },
                {
                    name: MAIN_CONTENT_SECTIONS.map,
                    elements: ['map']
                },
                {
                    name: MAIN_CONTENT_SECTIONS.rates_availability,
                    elements: ['rates-availability-calendar']
                },
                ...getOtherUnitsSection(multiUnitsLayoutValues, false)
            ]
        };
    }
);

const getMobileLayout = createSelector(
    [getTravelerSegments, getMultiUnitsLayoutValues, getPoliciesTestOn],
    ({coldStartUser}, multiUnitsLayoutValues, policiesTestOn) => {
        return {
            spacingBelow: {
                default: '32px',
                'listing-summary': '8px',
                'trip-details': '16px',
                'free-cancellation': '16px',
                'owner-details-mini': '16px',
                'booking-assistance': '24px',
                'listing-details': '88px',
                'chat-ui': '88px',
                'owner-details': '88px',
                'unit-spaces': '88px',
                amenities: '88px',
                'damage-policy-policies-section': '48px',
                'damage-policy': '16px',
                'cancellation-policy': '16px',
                'cancellation-policy-policies-section': '16px',
                'policies-headline': '24px',
                'trip-link': '48px'
            },
            order: [
                {
                    name: MAIN_CONTENT_SECTIONS.overview,
                    elements: [
                        'inline-urgency',
                        'listing-summary',
                        'trip-details',
                        'free-cancellation',
                        'owner-details-mini',
                        'booking-assistance',
                        'bear-stay-banner',
                        'listing-details',
                        'civil-code-disclaimer',
                        'inline-virtual-tour',
                        coldStartUser && !multiUnitsLayoutValues.showMultiUnits
                            ? 'reco-carousels'
                            : null,
                        'chat-ui',
                        'owner-details',
                        'auth-card',
                        'unit-spaces'
                    ].concat(
                        policiesTestOn
                            ? []
                            : [
                                  'house-rules',
                                  'cancellation-policy',
                                  'damage-policy',
                                  'cleaning-practices'
                              ]
                    )
                },
                ...(policiesTestOn
                    ? [
                          {
                              name: MAIN_CONTENT_SECTIONS.policies,
                              elements: [
                                  'policies-headline',
                                  'cancellation-policy-policies-section',
                                  'trip-link',
                                  'damage-policy-policies-section',
                                  'house-rules-wide',
                                  'cleaning-practices'
                              ]
                          }
                      ]
                    : []),
                ...getOtherUnitsSection(multiUnitsLayoutValues, true),
                {
                    name: MAIN_CONTENT_SECTIONS.reviews,
                    elements: ['traveler-sentiment-highlights', 'reviews']
                },
                {name: MAIN_CONTENT_SECTIONS.map, elements: ['map']},
                {name: MAIN_CONTENT_SECTIONS.rates_availability, elements: ['rates-availability']},
                {name: MAIN_CONTENT_SECTIONS.amenities, elements: ['amenities']},
                ...getOtherUnitsSection(multiUnitsLayoutValues, false)
            ]
        };
    }
);

const getLayout = createSelector(
    [getIsMobile, getDesktopLayout, getMobileLayout],
    (isMobile, desktopLayout, mobileLayout) => {
        return isMobile ? mobileLayout : desktopLayout;
    }
);
export default getLayout;
export {getLayout, getPoliciesTestOn, MAIN_CONTENT_SECTIONS};
